import { useOfficePresence } from "@/hooks/use-office-presence";
import { cn } from "@/lib/utils";

type PresenceStripProps = {
  className?: string;
  /** Compact bar for ready-room top chrome */
  compact?: boolean;
};

/**
 * “Who’s in HQ” strip for the ready room — polls /api/office/presence.
 */
export function PresenceStrip({ className, compact = false }: PresenceStripProps) {
  const { data, isLoading, isError } = useOfficePresence();
  const peers = data?.peers ?? [];
  const count = data?.count ?? peers.length;

  if (isError) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] text-white/45",
          className,
        )}
      >
        HQ offline
      </div>
    );
  }

  if (isLoading && !data) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] text-white/45",
          className,
        )}
      >
        Checking HQ…
      </div>
    );
  }

  if (count === 0) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] text-white/55",
          className,
        )}
        title="No one is in multiplayer HQ right now"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-white/35" />
        HQ is quiet
      </div>
    );
  }

  const shown = peers.slice(0, compact ? 5 : 8);
  const extra = count - shown.length;

  return (
    <div
      className={cn(
        "flex max-w-full items-center gap-2 rounded-full border border-white/12 bg-white/8 px-2.5 py-1.5",
        className,
      )}
      title={peers.map((p) => p.name).join(", ")}
    >
      <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-[#2EA86A]" />
      <span className="shrink-0 text-[11px] font-bold tracking-wide text-white/90">
        {count} in HQ
      </span>
      <div className="flex items-center -space-x-1.5">
        {shown.map((p) => (
          <span
            key={`${p.name}-${p.shirt}-${p.zoneId}`}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-[#001847] text-[9px] font-bold text-white shadow-sm"
            style={{ background: p.shirt }}
            title={`${p.name} · ${p.zoneId}`}
          >
            {initials(p.name)}
          </span>
        ))}
        {extra > 0 ? (
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-[#001847] bg-white/15 text-[9px] font-bold text-white">
            +{extra}
          </span>
        ) : null}
      </div>
      {!compact && shown[0] ? (
        <span className="hidden min-w-0 truncate text-[11px] text-white/60 sm:inline">
          {shown
            .slice(0, 3)
            .map((p) => p.name.split(" ")[0])
            .join(", ")}
          {count > 3 ? "…" : ""}
        </span>
      ) : null}
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return (parts[0]?.slice(0, 2) ?? "?").toUpperCase();
  const a = parts[0]?.[0] ?? "";
  const b = parts[1]?.[0] ?? "";
  return `${a}${b}`.toUpperCase() || "?";
}
